import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useCreators } from "@/context/CreatorsContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import {
  Play,
  Info,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Star,
  TrendingUp,
  Sparkles,
  Crown,
  Volume2,
  VolumeX,
} from "lucide-react";
import InAppBrowserRedirect from "@/components/InAppBrowserRedirect";

const isVideo = (url) => /\.(mp4|webm|ogg|mov)$/i.test(url || "");

/* ---------- Hero Banner (Netflix/MYM style carousel) ---------- */
const HeroBanner = ({ creators }) => {
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  const total = creators?.length || 0;
  const creator = total > 0 ? creators[index] : null;

  // Auto-rotate every 7s
  useEffect(() => {
    if (total <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % total), 7000);
    return () => clearInterval(t);
  }, [total]);

  if (!creator) return null;

  const cover = creator.coverImage || creator.avatar;
  const hasCoverVideo = !!creator.coverVideo || isVideo(creator.coverImage);
  const videoSrc = creator.coverVideo || (isVideo(creator.coverImage) ? creator.coverImage : null);

  const next = () => setIndex((i) => (i + 1) % total);
  const prev = () => setIndex((i) => (i - 1 + total) % total);

  return (
    <section className="relative h-[90vh] min-h-[600px] w-full overflow-hidden" data-testid="hero-banner">
      {/* Background media */}
      <div className="absolute inset-0">
        {hasCoverVideo && videoSrc ? (
          <video
            ref={videoRef}
            key={creator.id || creator.username}
            src={videoSrc}
            autoPlay
            loop
            muted={muted}
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <img
            src={cover}
            alt={creator.name}
            className="w-full h-full object-cover"
            loading="eager"
          />
        )}
        {/* Subtle bottom gradient only — image stays prominent */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black via-black/70 to-transparent" />
      </div>

      {/* Carousel arrows */}
      {total > 1 && (
        <>
          <button
            onClick={prev}
            data-testid="hero-prev-btn"
            aria-label="Anterior"
            className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur border border-white/20 flex items-center justify-center text-white/90 hover:text-white transition-all z-20"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button
            onClick={next}
            data-testid="hero-next-btn"
            aria-label="Siguiente"
            className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur border border-white/20 flex items-center justify-center text-white/90 hover:text-white transition-all z-20"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </>
      )}

      {/* Sound toggle */}
      {hasCoverVideo && videoSrc && (
        <button
          onClick={() => setMuted((m) => !m)}
          className="absolute right-4 top-20 w-9 h-9 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white flex items-center justify-center bg-black/30 backdrop-blur transition-all z-20"
          aria-label={muted ? "Activar sonido" : "Silenciar"}
          data-testid="hero-sound-toggle"
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        </button>
      )}

      {/* Content (bottom area, MYM style) */}
      <div className="absolute inset-x-0 bottom-0 px-6 sm:px-12 lg:px-16 pb-10 sm:pb-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 max-w-7xl mx-auto">
          <div className="min-w-0">
            {/* Free / Featured badge */}
            <div className="inline-flex items-center gap-1.5 mb-3 px-3 py-1 rounded-full bg-white/15 backdrop-blur border border-white/20 text-white text-xs font-semibold">
              {creator.featured ? (
                <>
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5">
                    <path d="M23 12l-2.44-2.78.34-3.68-3.61-.82L15.4 1.54 12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68z" fill="#0095F6" />
                    <path d="M10 17l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9z" fill="white" />
                  </svg>
                  Destacada
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3" />
                  Gratis
                </>
              )}
            </div>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white mb-3 drop-shadow-2xl leading-[0.95]">
              {creator.name}
            </h1>
            <div className="flex items-center gap-3 text-white/80 text-sm font-medium">
              <span className="inline-flex items-center gap-1.5">
                <span>@{creator.username}</span>
              </span>
              <span className="text-white/40">•</span>
              <span className="inline-flex items-center gap-1.5">
                <span>{creator.links?.length || 0} enlaces</span>
              </span>
              {creator.galleryImages?.length > 0 && (
                <>
                  <span className="text-white/40">•</span>
                  <span>{creator.galleryImages.length} fotos</span>
                </>
              )}
            </div>
            {creator.bio && (
              <p className="text-white/85 text-sm sm:text-base max-w-2xl mt-4 line-clamp-2 leading-relaxed">
                {creator.bio}
              </p>
            )}
          </div>

          {/* CTA button on the right */}
          <div className="flex-shrink-0">
            <Link
              to={`/${creator.username}`}
              data-testid="hero-discover-btn"
              className="inline-flex items-center justify-center gap-2 px-8 sm:px-12 py-4 bg-[#1d8ef7] hover:bg-[#1781e6] text-white font-bold rounded-xl transition-all hover:scale-[1.02] shadow-lg shadow-[#1d8ef7]/30 text-sm sm:text-base whitespace-nowrap"
            >
              Descubrir el perfil
            </Link>
          </div>
        </div>

        {/* Carousel dots */}
        {total > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-6">
            {creators.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Modelo ${i + 1}`}
                className={`h-1 rounded-full transition-all ${
                  i === index ? "w-8 bg-white" : "w-3 bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------- Card ---------- */
const CreatorCard = ({ creator }) => {
  const [hover, setHover] = useState(false);
  const videoRef = useRef(null);

  const cover = creator.coverImage || creator.avatar;
  const hasCoverVideo = !!creator.coverVideo || isVideo(creator.coverImage);
  const videoSrc = creator.coverVideo || (isVideo(creator.coverImage) ? creator.coverImage : null);

  useEffect(() => {
    if (!videoRef.current) return;
    if (hover) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [hover]);

  return (
    <Link
      to={`/${creator.username}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onTouchStart={() => setHover(true)}
      data-testid={`creator-card-${creator.username}`}
      className="relative flex-shrink-0 w-[160px] sm:w-[200px] md:w-[240px] aspect-[2/3] rounded-md overflow-hidden bg-white/5 group transition-transform duration-300 ease-out hover:scale-110 hover:z-20 hover:shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
    >
      {/* Cover image */}
      {hasCoverVideo && videoSrc && hover ? (
        <video
          ref={videoRef}
          src={videoSrc}
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img
          src={cover}
          alt={creator.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Featured badge */}
      {creator.featured && (
        <div className="absolute top-2 left-2 z-10 bg-yellow-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1">
          <Star className="w-2.5 h-2.5 fill-black" />
          TOP
        </div>
      )}

      {/* Hover info overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
        <h3 className="text-white font-bold text-base leading-tight mb-0.5 line-clamp-1">
          {creator.name}
        </h3>
        <p className="text-white/60 text-xs mb-2">@{creator.username}</p>
        {creator.bio && (
          <p className="text-white/80 text-xs line-clamp-2 mb-2 leading-snug">{creator.bio}</p>
        )}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-white text-black flex items-center justify-center">
            <Play className="w-3.5 h-3.5 fill-black ml-0.5" />
          </div>
          <span className="text-white text-xs font-semibold">Ver perfil</span>
        </div>
      </div>
    </Link>
  );
};

/* ---------- Row ---------- */
const Row = ({ title, icon: Icon, creators, accent = "white" }) => {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [creators]);

  if (!creators || creators.length === 0) return null;

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: (dir === "left" ? -1 : 1) * Math.round(el.clientWidth * 0.85), behavior: "smooth" });
  };

  return (
    <section className="relative px-6 sm:px-12 lg:px-16 group/row" data-testid={`row-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-4">
        {Icon && <Icon className={`w-5 h-5 ${accent === "yellow" ? "text-yellow-400" : "text-white"}`} />}
        <h2 className="text-xl sm:text-2xl font-bold text-white">{title}</h2>
      </div>

      <div className="relative">
        {/* Left arrow */}
        <button
          onClick={() => scroll("left")}
          className={`hidden md:flex absolute left-0 top-0 bottom-0 z-30 items-center justify-center w-12 bg-gradient-to-r from-black to-transparent opacity-0 group-hover/row:opacity-100 transition-opacity ${canLeft ? "" : "pointer-events-none !opacity-0"}`}
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-8 h-8 text-white" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth pt-12 -mt-12 pb-12 -mb-8"
          style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
        >
          {creators.map((c) => (
            <CreatorCard key={c.id || c.username} creator={c} />
          ))}
        </div>

        {/* Right arrow */}
        <button
          onClick={() => scroll("right")}
          className={`hidden md:flex absolute right-0 top-0 bottom-0 z-30 items-center justify-center w-12 bg-gradient-to-l from-black to-transparent opacity-0 group-hover/row:opacity-100 transition-opacity ${canRight ? "" : "pointer-events-none !opacity-0"}`}
          aria-label="Scroll right"
        >
          <ChevronRight className="w-8 h-8 text-white" />
        </button>
      </div>
    </section>
  );
};

/* ---------- Page ---------- */
const ExplorePage = () => {
  const { getActiveCreators, isLoading } = useCreators();
  const [searchQuery, setSearchQuery] = useState("");

  const activeCreators = getActiveCreators();

  const heroCreators = useMemo(() => {
    if (!activeCreators || activeCreators.length === 0) return [];
    const featured = activeCreators.filter((c) => c.featured);
    const pool = featured.length > 0 ? featured : activeCreators;
    return pool.slice(0, 6);
  }, [activeCreators]);

  // Filtered list when searching
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return activeCreators.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.username.toLowerCase().includes(q) ||
        (c.bio || "").toLowerCase().includes(q)
    );
  }, [searchQuery, activeCreators]);

  // Categories
  const featured = useMemo(
    () => activeCreators.filter((c) => c.featured),
    [activeCreators]
  );
  const top10 = useMemo(
    () =>
      [...activeCreators]
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, 10),
    [activeCreators]
  );
  const newest = useMemo(
    () =>
      [...activeCreators]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 12),
    [activeCreators]
  );
  const all = activeCreators;

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Explorar Modelos | JGlinks</title>
        <meta
          name="description"
          content="Descubre las modelos destacadas de JGlinks. Top 10, nuevas, y más en un panel exclusivo."
        />
      </Helmet>
      <InAppBrowserRedirect />
      <Header />

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      ) : activeCreators.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-white/50">
          Aún no hay modelos
        </div>
      ) : (
        <>
          {/* Hero */}
          {!searchQuery && <HeroBanner creators={heroCreators} />}

          {/* Search results */}
          {filtered ? (
            <section className="px-6 sm:px-12 lg:px-16 py-10">
              <div className="relative max-w-2xl mb-8">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Buscar modelo por nombre, usuario o bio..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="explore-search-input"
                  className="w-full pl-11 pr-10 py-3 bg-white/5 border border-white/10 rounded-full text-white placeholder-white/40 outline-none focus:border-white/30 transition-colors text-sm"
                />
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white"
                  aria-label="Limpiar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <h2 className="text-xl font-bold text-white mb-4" data-testid="search-results-heading">
                {filtered.length} resultado{filtered.length === 1 ? "" : "s"} para "{searchQuery}"
              </h2>
              {filtered.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                  {filtered.map((c) => (
                    <CreatorCard key={c.id || c.username} creator={c} />
                  ))}
                </div>
              ) : (
                <p className="text-white/50 py-12 text-center">
                  No encontramos modelos con ese nombre. Prueba con otro término.
                </p>
              )}
            </section>
          ) : (
            <div className="space-y-12 sm:space-y-16 py-10 pb-16 relative z-10">
              {/* Search bar within the rows container */}
              <div className="px-6 sm:px-12 lg:px-16">
                <div className="relative max-w-md">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    type="text"
                    placeholder="Buscar modelo..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="explore-search-input"
                    className="w-full pl-11 pr-10 py-2.5 bg-white/5 backdrop-blur border border-white/15 rounded-full text-white placeholder-white/40 outline-none focus:border-white/40 transition-colors text-sm"
                  />
                </div>
              </div>

              {/* Shorts CTA banner */}
              <div className="px-6 sm:px-12 lg:px-16">
                <Link
                  to="/shorts"
                  data-testid="explore-shorts-cta"
                  className="group block relative overflow-hidden rounded-3xl bg-gradient-to-r from-pink-500 via-rose-500 to-purple-600 p-6 sm:p-8 hover:shadow-2xl hover:shadow-pink-500/40 transition-shadow"
                >
                  <div className="relative z-10 flex items-center gap-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                      <span className="text-3xl sm:text-4xl">🎬</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-widest text-white/80 font-bold mb-1">Nuevo</p>
                      <h3 className="text-xl sm:text-2xl font-black text-white">Shorts en JGlinks</h3>
                      <p className="text-white/85 text-xs sm:text-sm mt-0.5">Clips verticales · descúbrenos en 60 segundos</p>
                    </div>
                    <ChevronRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform flex-shrink-0" />
                  </div>
                  {/* decorative blobs */}
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
                  <div className="absolute -bottom-10 left-1/3 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
                </Link>
              </div>

              <Row title="Modelos destacadas" icon={Crown} accent="yellow" creators={featured} />
              <Row title="Top 10 más vistas" icon={TrendingUp} creators={top10} />
              <Row title="Nuevas en JGlinks" icon={Sparkles} creators={newest} />
              <Row title="Todas las modelos" creators={all} />
            </div>
          )}
        </>
      )}

      <Footer />
    </div>
  );
};

export default ExplorePage;
